import React from 'react';
import { Alert, Typography } from 'antd';

import { useDisconnectCfTokenMutation, useGetConnectedCfTokensQuery } from '~features/cloudflare-integration';
import ConnectedCfTokensTable from './ConnectedCfTokensTable';
import DisconnectCfTokenModal from './DisconnectCfTokenModal';

const LOAD_FAILED_MESSAGE = 'Your connected Cloudflare tokens could not be loaded. Please try again later.';

const REVOKE_PATH_MESSAGE = 'To revoke the token in Cloudflare, go to My Profile. Then go to API Tokens.';

const deletedMessageFor = (domain: string) => `Prerender deleted its copy of the token for ${domain}.`;

/**
 * The flow is the tenant gate on the delete (PRE-3294), so a listed token whose
 * flow is gone answers 404 and cannot be hand-removed. The sweep still deletes
 * it on its delete date, and that is the path the customer is given — the screen
 * must not fail without one.
 */
const deleteFailedMessageFor = (domain: string) =>
  `Prerender could not delete the token for ${domain}. Try again. Prerender deletes this token automatically on its delete date.`;

function CloudflareTokensPage() {
  const { data, isFetching, isError } = useGetConnectedCfTokensQuery();
  const [disconnectCfToken, { isLoading: isDisconnecting }] = useDisconnectCfTokenMutation();
  const [pendingDomain, setPendingDomain] = React.useState<string | null>(null);
  const [disconnectingDomain, setDisconnectingDomain] = React.useState<string | null>(null);
  const [disconnectedDomain, setDisconnectedDomain] = React.useState<string | null>(null);
  const [disconnectError, setDisconnectError] = React.useState<string | null>(null);

  const tokens = data?.tokens ?? [];
  const total = data?.total ?? 0;

  const startDisconnect = (domain: string) => {
    setDisconnectedDomain(null);
    setDisconnectError(null);
    setPendingDomain(domain);
  };

  const confirmDisconnect = async () => {
    if (pendingDomain === null) {
      return;
    }

    setDisconnectingDomain(pendingDomain);
    try {
      await disconnectCfToken(pendingDomain).unwrap();
      setDisconnectedDomain(pendingDomain);
      setDisconnectError(null);
      setPendingDomain(null);
    } catch {
      setDisconnectError(deleteFailedMessageFor(pendingDomain));
    } finally {
      setDisconnectingDomain(null);
    }
  };

  return (
    <>
      <Typography.Title level={5}>Cloudflare Tokens</Typography.Title>
      <Typography.Paragraph type="secondary">
        The Cloudflare API tokens Prerender holds for your account. Prerender deletes each token automatically on
        its delete date.
      </Typography.Paragraph>
      {isError && <Alert type="error" showIcon message={LOAD_FAILED_MESSAGE} />}
      {disconnectedDomain !== null && (
        <Alert
          type="success"
          showIcon
          closable
          message={deletedMessageFor(disconnectedDomain)}
          description={REVOKE_PATH_MESSAGE}
          onClose={() => setDisconnectedDomain(null)}
        />
      )}
      <ConnectedCfTokensTable
        tokens={tokens}
        loading={isFetching}
        loadFailed={isError}
        onDisconnect={startDisconnect}
        disconnectingDomain={disconnectingDomain}
      />
      {total > tokens.length && (
        <Typography.Paragraph type="secondary">
          Showing {tokens.length} of {total} connected tokens
        </Typography.Paragraph>
      )}
      <DisconnectCfTokenModal
        open={pendingDomain !== null}
        domain={pendingDomain}
        onCancel={() => setPendingDomain(null)}
        onConfirm={confirmDisconnect}
        isLoading={isDisconnecting}
        error={disconnectError ?? ''}
        onErrorDismissed={() => setDisconnectError(null)}
      />
    </>
  );
}

export default CloudflareTokensPage;
